import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Car, Filter, Gauge, MapPin, Plus, Search, ShieldAlert, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/lib/axios';
import { resolveImageUrl } from '@/lib/listing';
import { useSeo, useJsonLd } from '../hooks/useSeo';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';
import { AdInlineList, AdSideRail } from '@/components/marketplace/ads/AdSlot';
import PromoBadges from '@/components/marketplace/PromoBadges';

const TABS = [
  { key: '', label: 'Todas' },
  { key: 'ex_tvde', label: 'Ex-TVDE' },
  { key: 'oportunidade', label: 'Oportunidades para TVDE' },
];

const ORDERS = [
  { key: 'recentes', label: 'Mais recentes' },
  { key: 'preco_asc', label: 'Preço mais baixo' },
  { key: 'preco_desc', label: 'Preço mais alto' },
  { key: 'km_asc', label: 'Menos quilómetros' },
  { key: 'ano_desc', label: 'Ano mais recente' },
];

const ESTADO_LABELS = {
  operacional: 'Operacional',
  operacional_anomalias: 'Operacional com anomalias',
  avariada: 'Avariada',
  imobilizada: 'Imobilizada',
  pecas: 'Para peças',
};

const VENDEDOR_LABELS = {
  particular: 'Particular', empresa: 'Empresa', profissional: 'Profissional', stand: 'Stand',
};

const FUEL_LABELS = {
  gasolina: 'Gasolina', gasoleo: 'Gasóleo', gpl: 'GPL', eletrico: 'Elétrico',
  hibrido: 'Híbrido', hibrido_plugin: 'Híbrido plug-in',
};

const EMPTY = {
  distrito: '', concelho: '', marca: '', modelo: '', preco_min: '', preco_max: '',
  ano_min: '', ano_max: '', km_max: '', combustivel: '', caixa: '', origem: '',
  estado: '', tipo_vendedor: '',
};

export const money = (v) => `${Number(v || 0).toLocaleString('pt-PT')} €`;
export const km = (v) => `${Number(v || 0).toLocaleString('pt-PT')} km`;

export function SaleCard({ item }) {
  return (
    <Link
      to={`/comprar-viaturas-tvde/${item.id}`}
      className={`group flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg ${
        (item.promocoes || []).includes('destaque') || (item.promocoes || []).includes('destaque_homepage')
          ? 'border-amber-300 ring-1 ring-amber-200'
          : 'border-slate-200'
      }`}
      data-testid={`sale-card-${item.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {item.foto ? (
          <img
            src={resolveImageUrl(item.foto)}
            alt={item.titulo}
            loading="lazy"
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-slate-400">
            <Car className="h-10 w-10" />
          </div>
        )}
        <div className="absolute left-2 top-2 flex flex-col gap-1">
          <span className="inline-flex w-fit items-center rounded-full bg-slate-900/85 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
            {item.categoria_label}
          </span>
          <PromoBadges promocoes={item.promocoes} testIdPrefix={`sale-${item.id}`} />
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="line-clamp-2 text-sm font-bold text-slate-900" data-testid={`sale-card-title-${item.id}`}>
          {item.titulo}
        </h3>
        <p className="text-2xl font-extrabold text-slate-900" data-testid={`sale-card-price-${item.id}`}>
          {money(item.preco)}
          {item.preco_negociavel && <span className="ml-1 text-xs font-semibold text-slate-500">negociável</span>}
        </p>
        <ul className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600">
          <li>{item.ano}</li>
          <li className="inline-flex items-center gap-1"><Gauge className="h-3 w-3" /> {km(item.quilometros)}</li>
          <li>{FUEL_LABELS[item.combustivel] || item.combustivel}</li>
          <li>{ESTADO_LABELS[item.estado_principal] || item.estado_principal}</li>
        </ul>
        <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-2 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {item.localizacao || '—'}</span>
          <span>{VENDEDOR_LABELS[item.tipo_vendedor] || '—'} · {item.nome_publico}</span>
        </div>
      </div>
    </Link>
  );
}

export default function MarketplaceBuyPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const categoria = params.get('categoria') || '';
  const [ordenar, setOrdenar] = useState(params.get('ordenar') || 'recentes');
  const [q, setQ] = useState(params.get('q') || '');
  const [filters, setFilters] = useState({ ...EMPTY });
  const [showFilters, setShowFilters] = useState(false);
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 24 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [facets, setFacets] = useState({ marcas: [], distritos: [] });
  const [page, setPage] = useState(1);

  useSeo({
    title: 'Comprar viaturas TVDE e Ex-TVDE em Portugal | Marketplace TVDE',
    description:
      'Viaturas Ex-TVDE e oportunidades para TVDE à venda em Portugal. Filtre por distrito, marca, preço, ano, quilómetros e estado da viatura.',
    path: '/comprar-viaturas-tvde',
  });
  useJsonLd('buy-page-jsonld', {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Comprar viaturas TVDE',
    url: 'https://marketplacetvde.pt/comprar-viaturas-tvde',
    numberOfItems: data.total,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = { ordenar, page, limit: 24 };
      if (categoria) query.categoria = categoria;
      if (q.trim()) query.q = q.trim();
      Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v !== null) query[k] = v; });
      const res = await api.get('/marketplace/venda/publicacoes', { params: query });
      setData(res.data);
      setError(false);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [categoria, ordenar, page, q, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/marketplace/venda/facetas').then((r) => setFacets(r.data)).catch(() => {}); }, []);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(data.total / (data.limit || 24))), [data]);

  const setCategoria = (key) => {
    const next = new URLSearchParams(params);
    if (key) next.set('categoria', key); else next.delete('categoria');
    setParams(next);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="buy-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 md:px-6">
          <Link to="/" className="text-sm font-bold text-slate-900" data-testid="buy-back-home">
            ← Marketplace TVDE
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/gerir-anuncio"
              className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              data-testid="buy-manage-link"
            >
              Gerir o meu anúncio
            </Link>
            <Link
              to="/vender-viatura"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3.5 py-2 text-xs font-bold text-white transition hover:bg-slate-800"
              data-testid="buy-sell-cta"
            >
              <Plus className="h-4 w-4" /> Vender uma viatura
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6">
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl" data-testid="buy-title">
          Comprar e vender viaturas
        </h1>
        <p className="mt-3 max-w-3xl text-base text-slate-600" data-testid="buy-intro">
          Viaturas <strong>Ex-TVDE</strong> (que já trabalharam no setor) e <strong>oportunidades para TVDE</strong>
          {' '}apresentadas pelos anunciantes. Toda a informação técnica e comercial é declarada por quem publica.
        </p>
        <p className="mt-2 max-w-3xl rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500" data-testid="buy-disclaimer">
          Qualquer compatibilidade com serviços ou categorias TVDE é declarada pelo anunciante e deve ser confirmada
          pelo interessado junto das plataformas e entidades aplicáveis.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="buy-tabs">
          {TABS.map((tabItem) => (
            <button
              key={tabItem.key || 'todas'}
              onClick={() => setCategoria(tabItem.key)}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                categoria === tabItem.key ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100'
              }`}
              data-testid={`buy-tab-${tabItem.key || 'todas'}`}
            >
              {tabItem.label}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load(); } }}
              placeholder="Pesquisar por marca ou modelo"
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm"
              data-testid="buy-search"
            />
          </div>
          <select
            value={ordenar}
            onChange={(e) => { setOrdenar(e.target.value); setPage(1); }}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm"
            data-testid="buy-sort"
          >
            {ORDERS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          <button
            onClick={() => setShowFilters((s) => !s)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700"
            data-testid="buy-toggle-filters"
          >
            <Filter className="h-4 w-4" /> Filtros
          </button>
        </div>

        {showFilters && (
          <div className="mt-3 grid gap-2 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-4" data-testid="buy-filters">
            <input list="distritos" placeholder="Distrito" value={filters.distrito} onChange={(e) => setFilters((f) => ({ ...f, distrito: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="buy-filter-distrito" />
            <datalist id="distritos">{facets.distritos.map((d) => <option key={d} value={d} />)}</datalist>
            <input placeholder="Concelho ou cidade" value={filters.concelho} onChange={(e) => setFilters((f) => ({ ...f, concelho: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="buy-filter-concelho" />
            <input list="marcas" placeholder="Marca" value={filters.marca} onChange={(e) => setFilters((f) => ({ ...f, marca: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="buy-filter-marca" />
            <datalist id="marcas">{facets.marcas.map((m) => <option key={m} value={m} />)}</datalist>
            <input placeholder="Modelo" value={filters.modelo} onChange={(e) => setFilters((f) => ({ ...f, modelo: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="buy-filter-modelo" />
            <input type="number" placeholder="Preço mínimo" value={filters.preco_min} onChange={(e) => setFilters((f) => ({ ...f, preco_min: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="buy-filter-preco-min" />
            <input type="number" placeholder="Preço máximo" value={filters.preco_max} onChange={(e) => setFilters((f) => ({ ...f, preco_max: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="buy-filter-preco-max" />
            <input type="number" placeholder="Ano mínimo" value={filters.ano_min} onChange={(e) => setFilters((f) => ({ ...f, ano_min: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="buy-filter-ano-min" />
            <input type="number" placeholder="Ano máximo" value={filters.ano_max} onChange={(e) => setFilters((f) => ({ ...f, ano_max: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="buy-filter-ano-max" />
            <input type="number" placeholder="Quilómetros máximos" value={filters.km_max} onChange={(e) => setFilters((f) => ({ ...f, km_max: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="buy-filter-km-max" />
            <select value={filters.combustivel} onChange={(e) => setFilters((f) => ({ ...f, combustivel: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="buy-filter-combustivel">
              <option value="">Combustível</option>
              {Object.entries(FUEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={filters.caixa} onChange={(e) => setFilters((f) => ({ ...f, caixa: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="buy-filter-caixa">
              <option value="">Caixa</option><option value="manual">Manual</option><option value="automatica">Automática</option>
            </select>
            <select value={filters.origem} onChange={(e) => setFilters((f) => ({ ...f, origem: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="buy-filter-origem">
              <option value="">Nacional ou importado</option><option value="nacional">Nacional</option><option value="importado">Importado</option>
            </select>
            <select value={filters.estado} onChange={(e) => setFilters((f) => ({ ...f, estado: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="buy-filter-estado">
              <option value="">Estado da viatura</option>
              {Object.entries(ESTADO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={filters.tipo_vendedor} onChange={(e) => setFilters((f) => ({ ...f, tipo_vendedor: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="buy-filter-vendedor">
              <option value="">Tipo de vendedor</option>
              {Object.entries(VENDEDOR_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <div className="flex gap-2 md:col-span-4">
              <button onClick={() => { setPage(1); load(); }} className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-bold text-white" data-testid="buy-apply-filters">Aplicar filtros</button>
              <button onClick={() => { setFilters({ ...EMPTY }); setQ(''); setPage(1); }} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700" data-testid="buy-clear-filters">Limpar</button>
            </div>
          </div>
        )}

        <p className="mt-5 text-sm text-slate-500" data-testid="buy-results-count">
          {loading ? 'A carregar…' : error ? 'Ligação indisponível' : `${data.total} viatura(s) encontrada(s)`}
        </p>

        {!loading && error && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center" data-testid="buy-error">
            <ShieldAlert className="mx-auto h-8 w-8 text-amber-400" />
            <p className="mt-3 text-sm font-semibold text-amber-900">Não foi possível carregar as viaturas. Pode ser um problema temporário de ligação.</p>
            <button onClick={() => load()} className="mt-4 rounded-xl bg-amber-500 px-4 py-2 text-sm font-bold text-white hover:bg-amber-600" data-testid="buy-retry">
              Tentar novamente
            </button>
          </div>
        )}

        {!loading && !error && data.items.length === 0 && (
          <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="buy-empty">
            <ShieldAlert className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm font-semibold text-slate-700">Ainda não há viaturas com estes critérios.</p>
            <p className="mt-1 text-sm text-slate-500">Experimente limpar os filtros ou seja o primeiro a publicar.</p>
            <button onClick={() => navigate('/vender-viatura')} className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white" data-testid="buy-empty-sell">
              Vender uma viatura
            </button>
          </div>
        )}

        {!error && data.items.length > 0 && (
        <div className="flex gap-4">
          <AdSideRail posicao="sales_list_sidebar_left" />
          <div className="min-w-0 flex-1">
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="buy-results">
              <AdInlineList posicao="sales_list_inline" pagina="/comprar-viaturas-tvde">
                {data.items.map((item) => <SaleCard key={item.id} item={item} />)}
              </AdInlineList>
            </div>
          </div>
          <AdSideRail posicao="sales_list_sidebar_right" />
        </div>
        )}

        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2" data-testid="buy-pagination">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-xl border border-slate-200 bg-white p-2 disabled:opacity-40" data-testid="buy-page-prev">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold text-slate-700" data-testid="buy-page-info">Página {page} de {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-xl border border-slate-200 bg-white p-2 disabled:opacity-40" data-testid="buy-page-next">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </main>

      <MarketplaceFooter />
    </div>
  );
}
